import { AxiosResponse } from "axios";
import { ImagePickerAsset } from "expo-image-picker";
import { useCallback, useEffect, useState } from "react";

import { useAxiosClient } from "@/hooks";
import { useFileUpload } from "@/hooks/storage";
import {
  ArchiveStudyRequest,
  CreateStudyRequest,
  GetStudiesRequest,
  ImportStudyRequest,
  UpdateStudyRequest,
} from "@/types/requests";
import { Folder, Study } from "@/types/resources";
import {
  CreateStudyResponse,
  GetStudiesResponse,
  GetStudyResponse,
  ImportStudyResponse,
  UpdateStudyResponse,
} from "@/types/responses";

const useCreateStudy = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const client = useAxiosClient("/studies");
  const { uploadFile } = useFileUpload();

  const createStudy = useCallback(
    async (
      data: Omit<CreateStudyRequest, "picture"> & {
        picture: ImagePickerAsset | null;
      }
    ) => {
      setError(null);
      setLoading(true);

      try {
        let picturePath: string | null = null;

        if (data.picture) {
          picturePath = await uploadFile({
            file: data.picture,
            folder: "studies/pictures",
            onProgress: setUploadProgress,
          });
        }

        const response = await client.post<
          CreateStudyResponse,
          AxiosResponse<CreateStudyResponse>,
          CreateStudyRequest
        >("", {
          description: data.description?.trim() || null,
          folder_id: data.folder_id,
          name: data.name.trim(),
          picture: picturePath,
          study_id: data.study_id,
          type: data.type,
          use_case: data.use_case ?? null,
        });
        return response.data.study;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
        setUploadProgress(0);
      }
    },
    [client, uploadFile]
  );

  return {
    createStudy,
    error,
    loading,
    uploadProgress,
  };
};

const useImportStudy = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/studies");

  const importStudy = useCallback(
    async (data: ImportStudyRequest): Promise<Study | null> => {
      setError(null);
      setLoading(true);

      try {
        const response = await client.post<
          ImportStudyResponse,
          AxiosResponse<ImportStudyResponse>,
          ImportStudyRequest
        >("/import", data);
        return response.data.study;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return {
    error,
    importStudy,
    loading,
  };
};

const useStudies = ({
  archived,
  folder_id,
  items_per_page = 10,
  name,
  page = 1,
  type,
  use_case,
}: GetStudiesRequest = {}) => {
  const [count, setCount] = useState<number>(0);
  const [currentFolder, setCurrentFolder] = useState<
    (Folder & { ancestors: Folder[] }) | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(true);
  const [studies, setStudies] = useState<Study[]>([]);

  const client = useAxiosClient("/studies");

  const getStudies = useCallback(
    (params: GetStudiesRequest) => {
      client
        .get<GetStudiesResponse>("", { params })
        .then((response) => {
          setCount(response.data.count);
          setCurrentFolder(response.data.current_folder);
          setFolders(response.data.folders);
          setStudies((prev) =>
            (params.page ?? 1) > 1
              ? [...prev, ...response.data.studies]
              : response.data.studies
          );
          setError(null);
        })
        .catch((e: unknown) => {
          setError(e instanceof Error ? e.message : String(e));
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [client]
  );

  // Reset the list during render (not in an effect) when the filters change.
  // The initial mount needs no reset since the state already starts empty.
  const filterKey = JSON.stringify([name, type, use_case, folder_id]);
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey);
  if (prevFilterKey !== filterKey) {
    setPrevFilterKey(filterKey);
    setStudies([]);
    setFolders([]);
    setCurrentFolder(null);
  }

  // Re-enter the loading state during render when any query param changes, so
  // the UI shows loading while refetching.
  const queryKey = JSON.stringify([
    page,
    items_per_page,
    name,
    type,
    use_case,
    folder_id,
    archived,
  ]);
  const [prevQueryKey, setPrevQueryKey] = useState(queryKey);
  if (prevQueryKey !== queryKey) {
    setPrevQueryKey(queryKey);
    setLoading(true);
    setError(null);
  }

  useEffect(() => {
    getStudies({
      archived: archived || undefined,
      folder_id: name ? undefined : folder_id,
      items_per_page,
      name: name || undefined,
      page,
      type,
      use_case,
    });
  }, [
    page,
    items_per_page,
    name,
    type,
    use_case,
    folder_id,
    archived,
    getStudies,
  ]);

  const refresh = useCallback(() => {
    setStudies([]);
    setFolders([]);
    setCurrentFolder(null);
    setLoading(true);
    setError(null);
    getStudies({
      archived: archived || undefined,
      folder_id: name ? undefined : folder_id,
      items_per_page,
      name: name || undefined,
      page: 1,
      type,
      use_case,
    });
  }, [getStudies, items_per_page, name, type, use_case, folder_id, archived]);

  return {
    count,
    currentFolder,
    error,
    folders,
    loading,
    refresh,
    setCount,
    setFolders,
    setStudies,
    studies,
  };
};

const useStudy = (studyId: string) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [study, setStudy] = useState<Study | null>(null);

  const client = useAxiosClient(`/studies/${studyId}`);

  const getStudy = useCallback(() => {
    if (!studyId) return;

    client
      .get<GetStudyResponse>("")
      .then((response) => {
        setStudy(response.data.study);
        setError(null);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [client, studyId]);

  // Re-enter the loading state during render (not in an effect) when the study
  // changes, mirroring the original effect (which only reset once past its
  // `!studyId` early return).
  const [prevStudyId, setPrevStudyId] = useState(studyId);
  if (prevStudyId !== studyId) {
    setPrevStudyId(studyId);
    if (studyId) {
      setLoading(true);
      setError(null);
    }
  }

  const refresh = useCallback(() => {
    if (studyId) {
      setLoading(true);
      setError(null);
    }
    getStudy();
  }, [getStudy, studyId]);

  useEffect(() => {
    getStudy();
  }, [getStudy]);

  return {
    error,
    loading,
    refresh,
    study,
  };
};

const useUpdateStudy = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/studies");

  const updateStudy = useCallback(
    async (studyId: string, data: UpdateStudyRequest) => {
      setError(null);
      setLoading(true);

      try {
        const response = await client.put<
          UpdateStudyResponse,
          AxiosResponse<UpdateStudyResponse>,
          UpdateStudyRequest
        >(`/${studyId}`, data);
        return response.data.study;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return {
    error,
    loading,
    updateStudy,
  };
};

const useArchiveStudy = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/studies");

  const archiveStudy = useCallback(
    async (studyId: string, archived: boolean) => {
      setError(null);
      setLoading(true);

      try {
        const response = await client.patch<
          UpdateStudyResponse,
          AxiosResponse<UpdateStudyResponse>,
          ArchiveStudyRequest
        >(`/${studyId}`, { archived });
        return response.data.study;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return {
    archiveStudy,
    error,
    loading,
  };
};

const useDeleteStudy = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/studies");

  const deleteStudy = useCallback(
    async (studyId: string) => {
      setError(null);
      setLoading(true);

      try {
        await client.delete(`/${studyId}`);
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return {
    deleteStudy,
    error,
    loading,
  };
};

export {
  useArchiveStudy,
  useCreateStudy,
  useDeleteStudy,
  useImportStudy,
  useStudies,
  useStudy,
  useUpdateStudy,
};
